import type {
  CloneModelPlanRequest,
  CloneModelPlanResponse,
  CreateModelPlanRequest,
  CreateModelPlanResponse,
  GetModelPlanPropagationRunResponse,
  GetOrgModelPlanResponse,
  ListModelPlanPropagationRunsResponse,
  ListModelPlansQuery,
  ListModelPlansResponse,
  ModelPlanDetailResponse,
  ModelPlanDryRunResponse,
  ModelPlanRepriceImpactGrain,
  ModelPlanRepriceImpactResponse,
  ModelPlanTargetsResponse,
  ModelPlanUnsyncedModelsResponse,
  PropagateModelPlanResponse,
  RollbackModelPlanResponse,
  UpdateModelPlanRequest,
  UpdateModelPlanResponse,
  UpdateOrgModelPlanRequest,
  UpdateOrgModelPlanResponse,
} from '@nadoutong/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { OpsApiError, opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for `/v1/ops/model-plans`
 * (epic #2292 / issue #2297) and the admin org assignment pair.
 *
 * Scope is NOT a client concern: ops-api pins the row scope per session
 * (lead/CM → sales_profiles.brand, admin → cross-brand). The filters
 * below are the client-tunable ones only — `status` for every tier,
 * `brand` additionally for admin. A scoped session that sent `brand`
 * would simply have it overwritten downstream.
 */

const BASE_PATH = '/v1/ops/model-plans';
const ORG_PLAN_PATH = '/v1/ops/admin/orgs';

export const MODEL_PLANS_QUERY_KEY = ['model-plans'] as const;
export const MODEL_PLAN_DETAIL_QUERY_KEY = ['model-plan-detail'] as const;
export const MODEL_PLAN_TARGETS_QUERY_KEY = ['model-plan-targets'] as const;
export const MODEL_PLAN_RUNS_QUERY_KEY = ['model-plan-runs'] as const;
export const MODEL_PLAN_RUN_QUERY_KEY = ['model-plan-run'] as const;
export const MODEL_PLAN_UNSYNCED_QUERY_KEY = ['model-plan-unsynced'] as const;
export const MODEL_PLAN_REPRICE_QUERY_KEY = ['model-plan-reprice'] as const;
export const ORG_MODEL_PLAN_QUERY_KEY = ['org-model-plan'] as const;

export interface ModelPlanListFilters {
  readonly status?: ListModelPlansQuery['status'] | undefined;
  /** Admin only — a scoped session's value is replaced by ops-api. */
  readonly brand?: string | undefined;
}

function buildListPath(filters: ModelPlanListFilters): string {
  const params = new URLSearchParams();
  if (filters.status !== undefined) {
    params.set('status', filters.status);
  }
  if (filters.brand !== undefined) {
    params.set('brand', filters.brand);
  }
  const query = params.toString();
  return query.length === 0 ? BASE_PATH : `${BASE_PATH}?${query}`;
}

export async function listModelPlans(
  filters: ModelPlanListFilters,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListModelPlansResponse> {
  return opsApiRequest<ListModelPlansResponse>(buildListPath(filters), { signal }, fetchImpl);
}

export async function getModelPlan(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPlanDetailResponse> {
  return opsApiRequest<ModelPlanDetailResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}`,
    { signal },
    fetchImpl,
  );
}

export async function getModelPlanTargets(
  brand: string | undefined,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPlanTargetsResponse> {
  const path =
    brand === undefined
      ? `${BASE_PATH}/targets`
      : `${BASE_PATH}/targets?${new URLSearchParams({ brand }).toString()}`;
  return opsApiRequest<ModelPlanTargetsResponse>(path, { signal }, fetchImpl);
}

export interface ModelPlanRepriceImpactQuery {
  readonly grain: ModelPlanRepriceImpactGrain;
  readonly target_id: string;
  readonly candidate_rate: number;
  readonly brand?: string | undefined;
}

export async function getModelPlanRepriceImpact(
  query: ModelPlanRepriceImpactQuery,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPlanRepriceImpactResponse> {
  const params = new URLSearchParams({
    grain: query.grain,
    target_id: query.target_id,
    candidate_rate: String(query.candidate_rate),
  });
  if (query.brand !== undefined) {
    params.set('brand', query.brand);
  }
  return opsApiRequest<ModelPlanRepriceImpactResponse>(
    `${BASE_PATH}/reprice-impact?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function createModelPlan(
  body: CreateModelPlanRequest,
  fetchImpl: typeof fetch,
): Promise<CreateModelPlanResponse> {
  return opsApiRequest<CreateModelPlanResponse>(BASE_PATH, { method: 'POST', body }, fetchImpl);
}

export async function updateModelPlan(
  id: string,
  body: UpdateModelPlanRequest,
  fetchImpl: typeof fetch,
  dryRun = false,
): Promise<UpdateModelPlanResponse | ModelPlanDryRunResponse> {
  const qs = dryRun ? '?dry_run=1' : '';
  if (dryRun) {
    return opsApiRequest<ModelPlanDryRunResponse>(
      `${BASE_PATH}/${encodeURIComponent(id)}${qs}`,
      { method: 'PUT', body },
      fetchImpl,
    );
  }
  return opsApiRequest<UpdateModelPlanResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}${qs}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function cloneModelPlan(
  id: string,
  body: CloneModelPlanRequest,
  fetchImpl: typeof fetch,
): Promise<CloneModelPlanResponse> {
  return opsApiRequest<CloneModelPlanResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/clone`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function archiveModelPlan(
  id: string,
  fetchImpl: typeof fetch,
): Promise<ModelPlanDetailResponse> {
  return opsApiRequest<ModelPlanDetailResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/archive`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function unarchiveModelPlan(
  id: string,
  fetchImpl: typeof fetch,
): Promise<ModelPlanDetailResponse> {
  return opsApiRequest<ModelPlanDetailResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/unarchive`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function propagateModelPlan(
  id: string,
  fetchImpl: typeof fetch,
): Promise<PropagateModelPlanResponse> {
  return opsApiRequest<PropagateModelPlanResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/propagate`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function listModelPlanPropagationRuns(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListModelPlanPropagationRunsResponse> {
  return opsApiRequest<ListModelPlanPropagationRunsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/propagation-runs`,
    { signal },
    fetchImpl,
  );
}

export async function getModelPlanPropagationRun(
  id: string,
  runId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetModelPlanPropagationRunResponse> {
  return opsApiRequest<GetModelPlanPropagationRunResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/propagation-runs/${encodeURIComponent(runId)}`,
    { signal },
    fetchImpl,
  );
}

export async function rollbackModelPlan(
  id: string,
  runId: string,
  fetchImpl: typeof fetch,
): Promise<RollbackModelPlanResponse> {
  return opsApiRequest<RollbackModelPlanResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/rollback/${encodeURIComponent(runId)}`,
    { method: 'POST' },
    fetchImpl,
  );
}

export async function getModelPlanUnsyncedModels(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ModelPlanUnsyncedModelsResponse> {
  return opsApiRequest<ModelPlanUnsyncedModelsResponse>(
    `${BASE_PATH}/${encodeURIComponent(id)}/unsynced-models`,
    { signal },
    fetchImpl,
  );
}

export async function getOrgModelPlan(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetOrgModelPlanResponse> {
  return opsApiRequest<GetOrgModelPlanResponse>(
    `${ORG_PLAN_PATH}/${encodeURIComponent(orgId)}/model-plan`,
    { signal },
    fetchImpl,
  );
}

export async function updateOrgModelPlan(
  orgId: string,
  body: UpdateOrgModelPlanRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateOrgModelPlanResponse> {
  return opsApiRequest<UpdateOrgModelPlanResponse>(
    `${ORG_PLAN_PATH}/${encodeURIComponent(orgId)}/model-plan`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export function useModelPlans(
  filters: ModelPlanListFilters,
  enabled = true,
): UseQueryResult<ListModelPlansResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLANS_QUERY_KEY, filters.status ?? 'any', filters.brand ?? 'scoped'],
    queryFn: ({ signal }) => listModelPlans(filters, fetchImpl, signal),
    enabled,
  });
}

export function useModelPlan(
  id: string | null,
  enabled = true,
): UseQueryResult<ModelPlanDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLAN_DETAIL_QUERY_KEY, id ?? ''],
    queryFn: ({ signal }) => getModelPlan(id ?? '', fetchImpl, signal),
    enabled: enabled && id !== null && id.length > 0,
  });
}

export function useModelPlanTargets(
  brand: string | undefined,
  enabled = true,
): UseQueryResult<ModelPlanTargetsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLAN_TARGETS_QUERY_KEY, brand ?? 'scoped'],
    queryFn: ({ signal }) => getModelPlanTargets(brand, fetchImpl, signal),
    enabled,
    // Sales floors move under this dialog: an operator configures them on
    // the pricing page and comes straight here. The client's 30s default
    // staleTime plus refetchOnWindowFocus:false would keep serving the
    // pre-edit snapshot for as long as the editor stays mounted — the
    // same trap useUpstreams documents, answered the same way.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useModelPlanRepriceImpact(
  query: ModelPlanRepriceImpactQuery | null,
  enabled = true,
): UseQueryResult<ModelPlanRepriceImpactResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [
      ...MODEL_PLAN_REPRICE_QUERY_KEY,
      query?.grain ?? '',
      query?.target_id ?? '',
      query?.candidate_rate ?? 0,
      query?.brand ?? 'scoped',
    ],
    queryFn: ({ signal }) => {
      if (query === null) {
        throw new Error('reprice-impact query is required when enabled');
      }
      return getModelPlanRepriceImpact(query, fetchImpl, signal);
    },
    enabled: enabled && query !== null && query.target_id.length > 0 && query.candidate_rate > 0,
  });
}

export function useModelPlanPropagationRuns(
  id: string | null,
  enabled = true,
): UseQueryResult<ListModelPlanPropagationRunsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLAN_RUNS_QUERY_KEY, id ?? ''],
    queryFn: ({ signal }) => listModelPlanPropagationRuns(id ?? '', fetchImpl, signal),
    enabled: enabled && id !== null && id.length > 0,
  });
}

export function useModelPlanPropagationRun(
  planId: string | null,
  runId: string | null,
  enabled = true,
): UseQueryResult<GetModelPlanPropagationRunResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLAN_RUN_QUERY_KEY, planId ?? '', runId ?? ''],
    queryFn: ({ signal }) =>
      getModelPlanPropagationRun(planId ?? '', runId ?? '', fetchImpl, signal),
    enabled:
      enabled && planId !== null && planId.length > 0 && runId !== null && runId.length > 0,
  });
}

export function useModelPlanUnsyncedModels(
  id: string | null,
  enabled = true,
): UseQueryResult<ModelPlanUnsyncedModelsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...MODEL_PLAN_UNSYNCED_QUERY_KEY, id ?? ''],
    queryFn: ({ signal }) => getModelPlanUnsyncedModels(id ?? '', fetchImpl, signal),
    enabled: enabled && id !== null && id.length > 0,
  });
}

export interface ModelPlanUnsyncedIndex {
  readonly byPlanId: ReadonlyMap<string, number> | undefined;
}

export function useModelPlanUnsyncedIndex(
  ids: readonly string[],
  enabled = true,
): ModelPlanUnsyncedIndex {
  const { fetchImpl } = useSession();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [...MODEL_PLAN_UNSYNCED_QUERY_KEY, id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        getModelPlanUnsyncedModels(id, fetchImpl, signal),
      enabled,
    })),
    combine: (results): ModelPlanUnsyncedIndex => {
      const byPlanId = new Map<string, number>();
      let complete = true;
      results.forEach((result, index) => {
        const id = ids[index];
        if (id === undefined) return;
        if (result.data === undefined) {
          if (result.isError) {
            byPlanId.set(id, 0);
            return;
          }
          complete = false;
          return;
        }
        byPlanId.set(id, result.data.skus.length);
      });
      return { byPlanId: complete ? byPlanId : undefined };
    },
  });
}

export interface ModelPlanCountsIndex {
  readonly byPlanId: ReadonlyMap<string, { entries: number; skus: number }> | undefined;
}

export function useModelPlanCountsIndex(
  ids: readonly string[],
  enabled = true,
): ModelPlanCountsIndex {
  const { fetchImpl } = useSession();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [...MODEL_PLAN_DETAIL_QUERY_KEY, id],
      queryFn: ({ signal }: { signal: AbortSignal }) => getModelPlan(id, fetchImpl, signal),
      enabled,
    })),
    combine: (results): ModelPlanCountsIndex => {
      const byPlanId = new Map<string, { entries: number; skus: number }>();
      let complete = true;
      results.forEach((result, index) => {
        const id = ids[index];
        if (id === undefined) return;
        if (result.data === undefined) {
          if (result.isError) {
            return;
          }
          complete = false;
          return;
        }
        byPlanId.set(id, {
          entries: result.data.model_plan.entries.length,
          skus: result.data.model_plan.skus.length,
        });
      });
      return { byPlanId: complete || byPlanId.size > 0 ? byPlanId : undefined };
    },
  });
}

export function useOrgModelPlan(
  orgId: string | null,
  enabled = true,
): UseQueryResult<GetOrgModelPlanResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...ORG_MODEL_PLAN_QUERY_KEY, orgId ?? ''],
    queryFn: ({ signal }) => getOrgModelPlan(orgId ?? '', fetchImpl, signal),
    enabled: enabled && orgId !== null && orgId.length > 0,
  });
}

function invalidatePlanQueries(qc: ReturnType<typeof useQueryClient>, id?: string): void {
  void qc.invalidateQueries({ queryKey: MODEL_PLANS_QUERY_KEY });
  if (id !== undefined) {
    void qc.invalidateQueries({ queryKey: [...MODEL_PLAN_DETAIL_QUERY_KEY, id] });
    void qc.invalidateQueries({ queryKey: [...MODEL_PLAN_RUNS_QUERY_KEY, id] });
    void qc.invalidateQueries({ queryKey: [...MODEL_PLAN_UNSYNCED_QUERY_KEY, id] });
  } else {
    void qc.invalidateQueries({ queryKey: MODEL_PLAN_DETAIL_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: MODEL_PLAN_RUNS_QUERY_KEY });
    void qc.invalidateQueries({ queryKey: MODEL_PLAN_UNSYNCED_QUERY_KEY });
  }
  void qc.invalidateQueries({ queryKey: ORG_MODEL_PLAN_QUERY_KEY });
}

export function useCreateModelPlan(): UseMutationResult<
  CreateModelPlanResponse,
  Error,
  CreateModelPlanRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createModelPlan(body, fetchImpl),
    onSuccess: () => {
      invalidatePlanQueries(qc);
    },
  });
}

export interface UpdateModelPlanVariables {
  readonly id: string;
  readonly body: UpdateModelPlanRequest;
  readonly dryRun?: boolean;
}

export function useUpdateModelPlan(): UseMutationResult<
  UpdateModelPlanResponse | ModelPlanDryRunResponse,
  Error,
  UpdateModelPlanVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, dryRun = false }) => updateModelPlan(id, body, fetchImpl, dryRun),
    onSuccess: (_data, variables) => {
      if (variables.dryRun === true) return;
      invalidatePlanQueries(qc, variables.id);
    },
  });
}

export interface CloneModelPlanVariables {
  readonly id: string;
  readonly body: CloneModelPlanRequest;
}

export function useCloneModelPlan(): UseMutationResult<
  CloneModelPlanResponse,
  Error,
  CloneModelPlanVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => cloneModelPlan(id, body, fetchImpl),
    onSuccess: () => {
      invalidatePlanQueries(qc);
    },
  });
}

export function useArchiveModelPlan(): UseMutationResult<
  ModelPlanDetailResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => archiveModelPlan(id, fetchImpl),
    onSuccess: (_data, id) => {
      invalidatePlanQueries(qc, id);
    },
  });
}

export function useUnarchiveModelPlan(): UseMutationResult<
  ModelPlanDetailResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => unarchiveModelPlan(id, fetchImpl),
    onSuccess: (_data, id) => {
      invalidatePlanQueries(qc, id);
    },
  });
}

export function usePropagateModelPlan(): UseMutationResult<
  PropagateModelPlanResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => propagateModelPlan(id, fetchImpl),
    onSuccess: (_data, id) => {
      invalidatePlanQueries(qc, id);
    },
  });
}

export interface RollbackModelPlanVariables {
  readonly id: string;
  readonly runId: string;
}

export function useRollbackModelPlan(): UseMutationResult<
  RollbackModelPlanResponse,
  Error,
  RollbackModelPlanVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, runId }) => rollbackModelPlan(id, runId, fetchImpl),
    onSuccess: (_data, variables) => {
      invalidatePlanQueries(qc, variables.id);
    },
  });
}

export interface UpdateOrgModelPlanVariables {
  readonly orgId: string;
  readonly body: UpdateOrgModelPlanRequest;
}

export function useUpdateOrgModelPlan(): UseMutationResult<
  UpdateOrgModelPlanResponse,
  Error,
  UpdateOrgModelPlanVariables
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => updateOrgModelPlan(orgId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: [...ORG_MODEL_PLAN_QUERY_KEY, variables.orgId] });
      invalidatePlanQueries(qc);
    },
  });
}

export interface ModelPlanManagedHint {
  readonly model_plan_id: string;
  readonly model_plan_name: string;
}

/** Parse a MODEL_PLAN_MANAGED 409 so the console can name the locking plan. */
export function modelPlanManagedFromError(err: unknown): ModelPlanManagedHint | null {
  if (!(err instanceof OpsApiError) || err.code !== 'MODEL_PLAN_MANAGED') {
    return null;
  }
  const id = err.details?.['model_plan_id'];
  const name = err.details?.['model_plan_name'];
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof name !== 'string' || name.length === 0) return null;
  return { model_plan_id: id, model_plan_name: name };
}

export function isModelPlanDryRunResponse(
  value: UpdateModelPlanResponse | ModelPlanDryRunResponse,
): value is ModelPlanDryRunResponse {
  return 'sku_deltas' in value && 'bound_active_code_count' in value && !('model_plan' in value);
}
